import { type QuoteDTO } from "~/features/orders/quoteList/api/fetchQuoteHistory/dto";
import * as ApiHelper from "~/common/utils/ApiHelper";
import { getOccTransport } from "~/common/api/occTransport";
import { constructUrl, resolveUrl } from "~/utils/url/urlResolver";
import { type QuoteData } from "~/domains/orders/types/modelsV2";
import {
  constructOrderDetailsUrl,
  isFromDifferentAccOrSalesOrg,
  transformOrderAccount,
} from "~/features/orders/orderList/api/fetchOrderHistory/fetchOrderHistory";
import { type PaginatedFetchFuncResponse } from "~/features/orders/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { type GroupedAccountsData } from "~/features/orders/common/DownloadOrderHistoryService/types";
import { type QuoteDetailsDTO } from "~/features/orders/quoteList/api/fetchOrderDetails/dto";

import { zero } from "~/utils/PriceUtils";

import { transformQuoteDetailLineItem } from "~/features/orders/quoteList/api/fetchOrderDetails/transformQuoteDetails";
import { toMoxyDateFromCalendarDate } from "~/common/types/MoxyDate";

export interface FetchQuoteHistoryProps {
  data: FetchQuoteHistoryRequestData;
  includeDetails?: boolean;
  skipToken?: string;
  signal?: AbortSignal;
}

export interface FetchQuoteHistoryRequestData {
  customerOrderId?: string;
  salesDocument?: string;
  lineCustomerOrderId?: string;
  partNumber?: string;
  matnr?: string;
  customerMatnr?: string;
  sdCreationStart?: string;
  sdCreatenEnd?: string;
  sdStatus: string;
  sdLineStatuses: string[];
  account?: GroupedAccountsData;
}

interface OrderHistoryResponseDTO {
  ocsalesDocumentDataList: QuoteDTO[];
  nextSkipToken: string | undefined;
  pageCount: number;
  totalCount: number;
}

interface ResponseData {
  results: QuoteData[];
  totalResults: number;
  pageCount: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FetchQuoteHistoryResponse extends PaginatedFetchFuncResponse<ResponseData> {}

export async function fetchQuoteHistory({
  data,
  skipToken,
  signal,
}: FetchQuoteHistoryProps): Promise<FetchQuoteHistoryResponse> {
  const { post, url: occUrl } = getOccTransport();
  let url = occUrl(`/quotes/search`);
  if (skipToken !== undefined) {
    const params = new URLSearchParams();
    params.set("skiptoken", skipToken);
    url = constructUrl(url, params);
  }

  // All orders filter option has id of "" - we need to filter it out
  const body = {
    ...data,
    sdCreationStart: data.sdCreationStart ? toMoxyDateFromCalendarDate(data.sdCreationStart) : undefined,
    sdCreatenEnd: data.sdCreatenEnd ? toMoxyDateFromCalendarDate(data.sdCreatenEnd) : undefined,
    sdLineStatuses: data.sdLineStatuses.filter(Boolean),
  };

  const response = await post(url, {
    body: JSON.stringify(body),
    headers: {
      "content-type": "application/json",
      CSRFToken: ApiHelper.getCsrfValue(),
    },
    signal,
  });

  const dto: OrderHistoryResponseDTO = await response.json();

  return {
    data: {
      results: dto.ocsalesDocumentDataList.map(transformQuote),
      totalResults: dto.totalCount,
      pageCount: dto.pageCount,
    },
    nextSkipToken: dto.nextSkipToken,
  };
}

export function constructQuoteDetailsUrl(soNumber: string | null | undefined, dto: QuoteDTO | QuoteDetailsDTO) {
  let url = "";
  if (soNumber) {
    url = resolveUrl(`/my-account/quotes/detail?id=${soNumber}`);
    if (isFromDifferentAccOrSalesOrg(dto)) {
      const params = new URLSearchParams();
      params.append("account", dto.account!.code);
      params.append("salesOrg", dto.account!.salesOrg);
      url = url + "&" + params.toString();
    }
  }
  return url;
}

export function transformQuote(dto: QuoteDTO): QuoteData {
  const orderData = transformQuoteProducts(dto);

  return {
    ...orderData,

    detailsUrl: constructQuoteDetailsUrl(dto.salesDocument, dto),
    orderAttainment: dto.quoteResponsibleName ?? "",
    isBundled: dto.isBundled ?? false,
    quoteNumber: dto.quoteNumber || dto.salesDocument || "", // treated as special, ONLY for Quotes, if the key "quoteNumber" is missing, then fallback to "salesDocument" else, empty
    quoteDocumentUrl: dto.quoteDocumentUrl ?? "",
  };
}

function transformQuoteProducts(dto: QuoteDTO): QuoteData {
  const products = dto.products ?? [];

  return {
    detailsUrl: constructOrderDetailsUrl(dto.salesDocument, dto),
    datePlaced: dto.datePlaced ? new Date(dto.datePlaced) : undefined,
    orderStatus: dto.sdStatus || "",
    orderStatusCode: dto.sdStatusCode || "",
    orderType: dto.orderType || "",
    soNumber: dto.salesDocument || "",
    poNumber: dto.customerOrderId || "",
    totalPrice: dto.price ? dto.price : zero(),
    products: products ? products.map(transformQuoteDetailLineItem) : [],
    hasSurcharge: dto.hasSurcharge || false,
    account: transformOrderAccount(dto.account),
    isBundled: false,
    orderAttainment: "",
    quoteNumber: dto.quoteNumber || "",
  };
}
